/**
 * Legacy bucketplace/ui에서 필요한 것들만 따로 옮겨두었습니다.
 * 새로만든 Sticky를 기획전에 적용할 수 있으면 제거하면 됩니다.
 */

import { useEffect, useRef } from 'react';
import { useContext, useContextSelector } from 'use-context-selector';

import { StickyControlContext, StickyValueContext } from '../contexts/internal/StickyContext';
import { StickyDirection, StickyState } from '../types';
import { quantizeSize } from '../utils/quantizeSize';

export const DEFAULT_STICKY_STATE: StickyState = {
  disabled: true,
  emulatedDisabled: true,
  bottom: false,
  isAbsolute: false,
  isNativeSticky: false,
  offset: 0,
  emulatedOffset: 0,
  height: null,
  width: null,
  beginY: 0,
};

export function useStickyHeight(
  ref: React.RefObject<HTMLElement | null>,
  direction: StickyDirection = 'top',
): { current: number, min: number, max: number } {
  const stickyControlContext = useContext(StickyControlContext);
  if (stickyControlContext == null) {
    throw new Error('StickyContext is not provided');
  }
  const stickyIdRef = useRef<string | null>(null);
  const stickyId = stickyIdRef.current || stickyControlContext.register();
  stickyIdRef.current = stickyId;
  const itemState = useContextSelector(StickyValueContext, (context) => {
    if (context == null) {
      return null;
    }
    return context.entries[stickyId];
  });
  useEffect(() => {
    stickyControlContext.update(
      stickyId,
      ref,
      {
        shouldInfluenceScroll: false,
        direction,
        offset: 0,
      },
      DEFAULT_STICKY_STATE,
    );
  }, [stickyControlContext.update, stickyId, ref, direction]);
  useEffect(() => {
    // Unregister on unmount
    return () => {
      stickyControlContext.unregister(stickyId);
    };
  }, [stickyId]);
  return {
    min: quantizeSize(itemState?.accumulatedOffsetRange?.[0] ?? 0),
    max: quantizeSize(itemState?.accumulatedOffsetRange?.[1] ?? 0),
    current: quantizeSize(itemState?.accumulatedOffset ?? 0),
  };
}
